/**
 * Colyseus connection wrapper.
 *
 * Deliberately *not* a React hook: the render loop reads `net.room.state`
 * directly every frame, while React only subscribes to the coarse UI slice in
 * netStore. That split is what keeps a 60fps duel from re-rendering the HUD
 * tree sixty times a second.
 */
import { Client, Room } from 'colyseus.js';
import {
  C2S,
  S2C,
  PROTOCOL_VERSION,
  type AnnounceMsg,
  type CastResultMsg,
  type CraftStartMsg,
  type ImpactMsg,
  type MatchResultMsg,
  type OpponentSpellMsg,
  type ParryResultMsg,
  type ProtocolErrorMsg,
  type RoundResultMsg,
  type SpellBookMsg,
  type SpellGrantedMsg,
  type WelcomeMsg,
} from '@spell/network-protocol';
import type { Spell } from '@spell/spell-schema';

/* ------------------------------------------------------------------ */
/* Minimal typed emitter                                               */
/* ------------------------------------------------------------------ */

export interface NetEvents {
  welcome: WelcomeMsg;
  spellGranted: SpellGrantedMsg;
  spellBook: SpellBookMsg;
  castResult: CastResultMsg;
  parryResult: ParryResultMsg;
  impact: ImpactMsg;
  roundResult: RoundResultMsg;
  matchResult: MatchResultMsg;
  craftStart: CraftStartMsg;
  opponentSpell: OpponentSpellMsg;
  announce: AnnounceMsg;
  rtcSignal: { kind: 'offer' | 'answer' | 'candidate'; payload: string };
  protocolError: ProtocolErrorMsg;
  stateChange: void;
  disconnected: { code: number };
}

type Handler<K extends keyof NetEvents> = (payload: NetEvents[K]) => void;

class Emitter {
  private map = new Map<string, Set<(p: unknown) => void>>();

  on<K extends keyof NetEvents>(event: K, fn: Handler<K>): () => void {
    let set = this.map.get(event as string);
    if (!set) {
      set = new Set();
      this.map.set(event as string, set);
    }
    set.add(fn as (p: unknown) => void);
    return () => set!.delete(fn as (p: unknown) => void);
  }

  emit<K extends keyof NetEvents>(event: K, payload: NetEvents[K]) {
    const set = this.map.get(event as string);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[net] handler for ${String(event)} threw`, err);
      }
    }
  }

  clear() {
    this.map.clear();
  }
}

/* ------------------------------------------------------------------ */
/* Server address                                                      */
/* ------------------------------------------------------------------ */

export function serverHttpUrl(): string {
  const explicit = import.meta.env.VITE_SERVER_URL as string | undefined;
  if (explicit) return explicit.replace(/\/$/, '');
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:2567`;
}

function serverWsUrl(): string {
  return serverHttpUrl().replace(/^http/, 'ws');
}

/* ------------------------------------------------------------------ */
/* The connection                                                      */
/* ------------------------------------------------------------------ */

export class SpellNet {
  readonly events = new Emitter();
  room: Room | null = null;
  sessionId = '';
  roomCode = '';

  /** serverTime - clientTime, refined by the ping loop. */
  clockOffset = 0;
  rtt = 0;

  private client = new Client(serverWsUrl());
  private pingTimer: number | null = null;
  private lastPingSentAt = 0;
  private bestRtt = Number.POSITIVE_INFINITY;

  /**
   * @param speedScale the room's projectile speed rule. Fixed for the room's
   *   lifetime and published to both duellists; the server clamps it.
   */
  async createRoom(name: string, speedScale: number): Promise<string> {
    const code = await this.reserveCode();
    const room = await this.client.create('duel', { code, name, mode: 'duel', speedScale });
    this.attach(room, code);
    return code;
  }

  /**
   * Open a solo practice room.
   *
   * It is the same room class running the same authoritative simulation, with a
   * training dummy in the far stance and no clock — which is the point: what you
   * practise is graded by the code that decides a real duel.
   */
  async createPractice(name: string, speedScale: number): Promise<string> {
    const code = await this.reserveCode();
    const room = await this.client.create('duel', { code, name, mode: 'practice', speedScale });
    this.attach(room, code);
    return code;
  }

  private async reserveCode(): Promise<string> {
    const res = await fetch(`${serverHttpUrl()}/api/rooms/code`, { method: 'POST' });
    if (!res.ok) throw new Error('서버에서 방 코드를 받지 못했습니다.');
    const { code } = (await res.json()) as { code: string };
    return code;
  }

  async joinRoom(code: string, name: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    const probe = await fetch(`${serverHttpUrl()}/api/rooms/${normalized}`);
    if (probe.status === 404) throw new Error('그런 방이 없습니다. 코드를 확인하세요.');
    if (!probe.ok) throw new Error('방 코드가 올바르지 않습니다.');
    const info = (await probe.json()) as { clients: number };
    if (info.clients >= 2) throw new Error('이미 두 명이 대련 중인 방입니다.');
    const room = await this.client.join('duel', { code: normalized, name });
    this.attach(room, normalized);
  }

  private attach(room: Room, code: string) {
    this.room = room;
    this.sessionId = room.sessionId;
    this.roomCode = code;

    room.onMessage(S2C.PONG, (msg: { t: number; serverTime: number }) => {
      const now = SpellNet.now();
      const rtt = now - msg.t;
      // Keep the offset from the *fastest* round trip observed; that sample has
      // the least asymmetry and therefore the least clock error.
      if (rtt < this.bestRtt) {
        this.bestRtt = rtt;
        this.clockOffset = msg.serverTime + rtt / 2 - now;
      }
      this.rtt = rtt;
    });

    const relay = <K extends keyof NetEvents>(type: string, event: K) => {
      room.onMessage(type, (payload: NetEvents[K]) => this.events.emit(event, payload));
    };
    relay(S2C.WELCOME, 'welcome');
    relay(S2C.SPELL_GRANTED, 'spellGranted');
    relay(S2C.SPELL_BOOK, 'spellBook');
    relay(S2C.CAST_RESULT, 'castResult');
    relay(S2C.PARRY_RESULT, 'parryResult');
    relay(S2C.IMPACT, 'impact');
    relay(S2C.ROUND_RESULT, 'roundResult');
    relay(S2C.MATCH_RESULT, 'matchResult');
    relay(S2C.CRAFT_START, 'craftStart');
    relay(S2C.OPPONENT_SPELL, 'opponentSpell');
    relay(S2C.ANNOUNCE, 'announce');
    relay(S2C.RTC_SIGNAL, 'rtcSignal');
    relay(S2C.ERROR, 'protocolError');

    room.onStateChange(() => this.events.emit('stateChange', undefined));
    room.onLeave((code) => {
      this.stopPing();
      this.events.emit('disconnected', { code });
    });

    this.startPing();
  }

  /**
   * Hand the room the loadout authored before it existed. Both spells come from
   * browser storage, so the server re-prices both before accepting either.
   */
  identify(name: string, parrySpell: Spell | null, attackSpell: Spell | null) {
    this.room?.send(C2S.IDENTIFY, {
      name,
      protocolVersion: PROTOCOL_VERSION,
      parrySpell,
      attackSpell,
    });
  }

  send(type: string, payload?: unknown) {
    this.room?.send(type, payload as never);
  }

  /** Map a local performance-epoch timestamp into server time. */
  toServerTime(clientTime: number): number {
    return clientTime + this.clockOffset;
  }

  /**
   * Epoch-aligned, monotonic clock. Every client timestamp that crosses the
   * wire uses this — mixing it with Date.now() would let wall-clock jumps show
   * up as parry timing errors.
   */
  static now(): number {
    return performance.timeOrigin + performance.now();
  }

  private startPing() {
    this.stopPing();
    const tick = () => {
      this.lastPingSentAt = SpellNet.now();
      this.room?.send(C2S.PING, {
        t: this.lastPingSentAt,
        rtt: Number.isFinite(this.bestRtt) ? this.bestRtt : undefined,
      });
    };
    tick();
    this.pingTimer = window.setInterval(tick, 1500);
  }

  private stopPing() {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  async leave() {
    this.stopPing();
    try {
      await this.room?.leave(true);
    } catch {
      /* already gone */
    }
    this.room = null;
    this.sessionId = '';
    this.events.clear();
    this.bestRtt = Number.POSITIVE_INFINITY;
    this.clockOffset = 0;
  }
}

export const net = new SpellNet();
